import { useNotification } from '@/containers'
import { assetUrl } from '@/utils/asset-url'
import type { ImageProps } from 'next/image'
import Image from 'next/image'
import Head from 'next/head'
import { omit } from 'lodash'
import { version as reactVersion } from 'react'

import type { StaticImport } from 'next/dist/shared/lib/get-img-props'
const isSvg = (src: string | StaticImport): boolean => {
  if (typeof src !== 'string') return false
  return src.trim().toLowerCase().endsWith('.svg')
}

// Các mốc width cho srcset ảnh CDN lớn (xem nhánh responsive trong component).
const RESPONSIVE_MIN_WIDTH = 400
const RESPONSIVE_MAX_WIDTH = 1920
const RESPONSIVE_WIDTHS = [320, 480, 640, 828, 1080, 1280, 1600, 1920]
// Prop riêng của next/image, không được lọt xuống thẻ <img>.
const NEXT_IMAGE_ONLY_PROPS = [
  'src',
  'width',
  'height',
  'fill',
  'loader',
  'quality',
  'priority',
  'loading',
  'placeholder',
  'blurDataURL',
  'unoptimized',
  'onLoadingComplete',
  'layout',
  'objectFit',
  'objectPosition',
  'lazyBoundary',
  'lazyRoot',
  'sizes',
  'style',
  'className',
  'alt',
  'fetchPriority',
] as const

// Thuộc tính fetchpriority cho <img>/<link> tự render. Sao chép cách của
// next/image: React < 18.3 phải dùng chữ thường `fetchpriority`, từ 18.3 dùng
// camelCase, nếu không React cảnh báo "Invalid DOM property" và bỏ thuộc tính.
type FetchPriority = NonNullable<ImageProps['fetchPriority']>
const fetchPriorityAttr = (
  fetchPriority?: FetchPriority
): { fetchPriority?: FetchPriority; fetchpriority?: FetchPriority } => {
  if (!fetchPriority) return {}
  const [major, minor] = reactVersion.split('.').map(v => parseInt(v, 10))
  return major > 18 || (major === 18 && minor >= 3)
    ? { fetchPriority }
    : { fetchpriority: fetchPriority }
}

const ImageOptimize = ({
  alt,
  thumbnail = false,
  lazyLoad = true,
  ...rest
}: ImageProps & {
  alt: string
  thumbnail?: boolean
  lazyLoad?: boolean
}) => {
  const { systemConfig } = useNotification()
  const qtt =
    systemConfig?.imageQlt && systemConfig?.imageQlt != ''
      ? systemConfig?.imageQlt
      : 70
  const width =
    systemConfig?.imageThumbWidthMobile &&
    systemConfig?.imageThumbWidthMobile != ''
      ? systemConfig?.imageThumbWidthMobile
      : 275
  const thumbParam = thumbnail ? `&width=${width}` : ''

  const baseSrc =
    rest.src && typeof rest.src === 'string'
      ? rest.src
      : systemConfig?.imagePlaceholder ||
        process.env.NEXT_PUBLIC_IMAGE_PLACEHOLDER ||
        ''

  // Không có đường dẫn ảnh (vd. icon mega menu chưa cấu hình VÀ
  // systemConfig.imagePlaceholder rỗng): trước đây vẫn render <img src="">
  // -> trình duyệt hiện icon ảnh vỡ và tải lại chính trang hiện tại
  // ("/?format=webp&quality=..."). Trả khối giữ đúng chỗ theo width/height.
  if (!baseSrc) {
    const placeholderWidth = Number(rest.width) || 0
    const placeholderHeight = Number(rest.height) || 0
    return (
      <span
        aria-hidden='true'
        className={`${rest.className ?? ''} imgOtm`}
        style={{
          display: 'inline-block',
          maxWidth: '100%',
          ...(placeholderWidth ? { width: `${placeholderWidth}px` } : {}),
          ...(placeholderWidth && placeholderHeight
            ? { aspectRatio: `${placeholderWidth} / ${placeholderHeight}` }
            : {}),
          ...rest.style,
        }}
      />
    )
  }

  const srcWithParams =
    isSvg(baseSrc) || baseSrc.includes('format=webp')
      ? baseSrc + (thumbnail ? `?${thumbParam.slice(1)}` : '')
      : baseSrc + `?format=webp&quality=${qtt}${thumbParam}`
  // Ảnh CDN lớn (banner, slider...): trả srcset theo `width` để trình duyệt
  // chọn bản vừa khung thay vì tải ảnh gốc (có banner gốc 7000px, ~1.8 MB).
  // CDN (AWS image handler) resize theo `width` và giữ tỉ lệ - KHÔNG truyền
  // `height` vì CDN sẽ ép/cắt ảnh vào đúng khung width x height.
  // Chỉ áp cho ảnh không phải thumbnail, không `fill`, khai báo width > 400
  // (ảnh gallery PDP khai báo đúng 400 được loại ra: thumbnail bên dưới dùng
  // chung URL gốc, đổi sang bản resize sẽ khiến ảnh bị tải 2 lần):
  // đã đo trên production, loại ảnh này luôn hiển thị <= width khai báo nên
  // không bị mờ. next.config đặt images.unoptimized nên next/image không tự
  // sinh srcset -> render <img> với đúng các thuộc tính next/image vẫn render.
  const declaredWidth = Number(rest.width) || 0
  if (
    !thumbnail &&
    !rest.fill &&
    declaredWidth > RESPONSIVE_MIN_WIDTH &&
    !isSvg(baseSrc) &&
    /^https?:\/\//i.test(baseSrc) &&
    !baseSrc.includes('?')
  ) {
    const urlAt = (w: number) =>
      `${baseSrc}?format=webp&quality=${qtt}&width=${w}`
    const widths = Array.from(
      new Set([
        ...RESPONSIVE_WIDTHS,
        Math.min(declaredWidth, RESPONSIVE_MAX_WIDTH),
      ])
    ).sort((a, b) => a - b)
    const srcSet = widths.map(w => `${urlAt(w)} ${w}w`).join(', ')
    const sizes =
      rest.sizes && rest.sizes !== '100%'
        ? rest.sizes
        : `(max-width: ${declaredWidth}px) 100vw, ${declaredWidth}px`
    const src = urlAt(Math.min(declaredWidth, RESPONSIVE_MAX_WIDTH))
    return (
      <>
        {rest.priority && (
          <Head>
            <link
              key={`__nimg-${src}`}
              rel='preload'
              as='image'
              href={src}
              imageSrcSet={srcSet}
              imageSizes={sizes}
              {...fetchPriorityAttr(rest.fetchPriority)}
            />
          </Head>
        )}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          {...omit(rest, NEXT_IMAGE_ONLY_PROPS)}
          {...fetchPriorityAttr(rest.fetchPriority)}
          alt={alt ?? systemConfig?.websiteBrandName ?? ''}
          width={declaredWidth}
          height={Number(rest.height) || undefined}
          decoding='async'
          data-nimg='1'
          className={`${rest.className ?? ''} imgOtm`}
          style={{ color: 'transparent', ...rest.style }}
          loading={rest.priority ? 'eager' : lazyLoad ? 'lazy' : 'eager'}
          sizes={sizes}
          srcSet={srcSet}
          src={src}
        />
      </>
    )
  }

  return (
    <Image
      {...rest}
      className={`${rest.className ?? ''} imgOtm`}
      // alt='' tường minh (ảnh trang trí) phải được giữ nguyên - `||` coi
      // '' là falsy nên trước đây bị đè bằng tên brand, gây alt sai (vd
      // icon thẻ Visa/Mastercard/JCB đều ra alt="Pico").
      alt={alt ?? systemConfig?.websiteBrandName ?? ''}
      width={rest.width ?? 0}
      height={rest.height ?? 0}
      sizes='100%'
      // Đường dẫn nội bộ (file trong public/) đi qua CDN; URL tuyệt đối giữ
      // nguyên. Gắn ở đây chứ không ở nơi gọi để src nội bộ không lọt vào
      // nhánh srcset phía trên (nhánh đó chỉ dành cho ảnh của CDN ảnh).
      src={assetUrl(srcWithParams)}
      loading={rest.priority ? 'eager' : lazyLoad ? 'lazy' : 'eager'}
    />
  )
}
export default ImageOptimize
